import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createTreasuryPaymentIntent,
  listTreasuryPaymentIntents,
  reviewTreasuryPaymentIntent,
  submitTreasuryPaymentIntent,
} from '../../services/TreasuryService';
import { normalizePaymentIntent } from './treasuryModels';

export function useTreasuryPaymentIntents({
  token,
  clearMessages,
  communityId,
  setError,
  setSuccess,
  setSubmitting,
}) {
  const alive = useRef(false), readVersion = useRef(0);
  const options = useMemo(() => ({token, ...(communityId ? {params: {communityId}} : {})}), [token, communityId]);
  useEffect(() => {alive.current = true; return () => {alive.current = false; readVersion.current += 1;};}, []);
  const [paymentIntents, setPaymentIntents] = useState([]);
  const lastWalletContribution = null;

  const loadPaymentIntents = useCallback(async ({ limit = 25 } = {}) => {
    const request = ++readVersion.current;
    if (!token) {
      setPaymentIntents([]);
      return [];
    }

    try {
      const payload = await listTreasuryPaymentIntents({ ...options, limit });
      const rows = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      if (!alive.current || request !== readVersion.current) return [];
      const normalized = rows.map((item) => normalizePaymentIntent(item)).filter(Boolean);
      setPaymentIntents(normalized);
      return normalized;
    } catch (err) {
      if (!alive.current || request !== readVersion.current) return [];
      setPaymentIntents([]); setError(err.message || 'Unable to load payment history.');
      console.error('Failed to load treasury payment intents:', err);
      return [];
    }
  }, [options, setError, token]);

  const createPaymentIntent = useCallback(async ({ fundId, expectedAmount, suggestedAmount, memberNote }) => {
    clearMessages();
    setSubmitting(true);

    try {
      const response = await createTreasuryPaymentIntent({
        ...options,
        fundId,
        expectedAmount,
        suggestedAmount,
        memberNote,
      });
      if (!alive.current) return {success: false};
      const normalized = normalizePaymentIntent(response?.data || response);
      if (normalized) {
        setPaymentIntents((prev) => [normalized, ...prev.filter((item) => item.id !== normalized.id)]);
      }
      setSuccess('Payment intent created. Use the reference exactly as shown.');
      return { success: true, data: normalized };
    } catch (err) {
      if (!alive.current) return {success: false};
      const message = err?.message || 'Failed to create payment intent.';
      setError(message);
      return { success: false, message };
    } finally {
      if (alive.current) setSubmitting(false);
    }
  }, [clearMessages, setError, setSubmitting, setSuccess, options]);

  const submitPaymentIntent = useCallback(async ({ intentId, walletAddress, txHash }) => {
    clearMessages();
    setSubmitting(true);

    try {
      const response = await submitTreasuryPaymentIntent({
        ...options,
        intentId,
        walletAddress,
        txHash,
      });
      if (!alive.current) return {success: false};
      const normalized = normalizePaymentIntent(response?.data || response);
      if (normalized) {
        setPaymentIntents((prev) => prev.map((item) => (item.id === normalized.id ? normalized : item)));
      }
      setSuccess('Payment submission recorded. We will verify it against treasury sync data.');
      return { success: true, data: normalized };
    } catch (err) {
      if (!alive.current) return {success: false};
      const message = err?.message || 'Failed to submit payment intent.';
      if (/user rejected|rejected the request|denied/i.test(message)) {
        setError('Wallet transaction was cancelled before submission.');
      } else {
        setError(message);
      }
      return { success: false, message };
    } finally {
      if (alive.current) setSubmitting(false);
    }
  }, [clearMessages, setError, setSubmitting, setSuccess, options]);

  const reviewPaymentIntent = useCallback(async ({ intentId, operatorReviewStatus, operatorReviewNote, status }) => {
    clearMessages();
    setSubmitting(true);

    try {
      const response = await reviewTreasuryPaymentIntent({
        ...options,
        intentId,
        operatorReviewStatus,
        operatorReviewNote,
        status,
      });
      if (!alive.current) return {success: false};
      const normalized = normalizePaymentIntent(response?.data || response);
      if (normalized) {
        setPaymentIntents((prev) => prev.map((item) => (item.id === normalized.id ? normalized : item)));
      }
      setSuccess(status === 'verified' ? 'Payment intent marked verified.' : 'Payment intent review saved.');
      return { success: true, data: normalized };
    } catch (err) {
      if (!alive.current) return {success: false};
      const message = err?.message || 'Failed to review payment intent.';
      setError(message);
      return { success: false, message };
    } finally {
      if (alive.current) setSubmitting(false);
    }
  }, [clearMessages, setError, setSubmitting, setSuccess, options]);

  const sendWalletContribution = useCallback(async () => {
    const message = 'Crypto contributions are unavailable. Use Community Fund for supported contribution instructions. Existing crypto records remain available as history.';
    clearMessages(); setError(message);
    return {success: false, message};
  }, [clearMessages, setError]);

  return {
    paymentIntents,
    lastWalletContribution,
    loadPaymentIntents,
    createPaymentIntent,
    submitPaymentIntent,
    reviewPaymentIntent,
    sendWalletContribution,
  };
}
